import React, { useEffect, useState } from "react";
import { Plus, Edit3, Trash2, X, ImagePlus, Upload, Trash, Download, Percent, Cloud, Loader2, Wand2, Tags, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice } from "../lib/api";
import AdminLayout from "../components/AdminLayout";

const empty = {
  name: "",
  brand: "",
  category: "Tote",
  price: "",
  cost: "",
  description: "",
  color: "",
  material: "",
  stock: 1,
  images: [],
  featured: false,
  authenticator: "",
  certificate_number: "",
};

const CATEGORIES = ["Tote", "Shoulder", "Crossbody", "Clutch", "Mini", "Top Handle", "Bucket", "Backpack", "Handbag"];

const SAMPLE_IMPORT_JSON = `[
  {
    "name": "Lambskin Top Handle",
    "brand": "Atelier Saint-Honoré",
    "category": "Top Handle",
    "price": 2400,
    "color": "Noir",
    "material": "Lambskin",
    "stock": 1,
    "description": "Hand-finished, structured silhouette.",
    "images": ["https://images.unsplash.com/photo-1.jpg"],
    "featured": true,
    "authenticator": "Real Authentication",
    "certificate_number": "RA-12345"
  }
]`;

export default function Admin() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null or product object or "new"
  const [form, setForm] = useState(empty);
  const [imgInput, setImgInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");
  const [importReplace, setImportReplace] = useState(false);
  const [showMarkup, setShowMarkup] = useState(false);
  const [markup, setMarkup] = useState({
    percent: "15",
    scope: "all", // all | brand | category | price
    brand: "",
    category: "",
    min_price: "",
    max_price: "",
  });
  const [markupPreview, setMarkupPreview] = useState(null); // {affected, sample}
  const [markupRunning, setMarkupRunning] = useState(false);
  const [showMigrate, setShowMigrate] = useState(false);
  const [migrateStatus, setMigrateStatus] = useState(null); // ImageMigrationStatus
  const [migrateStarting, setMigrateStarting] = useState(false);

  // Bulk recategorization + auto-classify
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkCategory, setBulkCategory] = useState("");
  const [showAutoClassify, setShowAutoClassify] = useState(false);
  const [classifyPreview, setClassifyPreview] = useState(null);
  const [classifyRunning, setClassifyRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/products", { params: { limit: 1000 } });
      setProducts(r.data || []);
    } catch (_) {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setForm(empty);
    setEditing("new");
  };

  const startEdit = (p) => {
    setForm({
      name: p.name,
      brand: p.brand,
      category: p.category,
      price: p.price,
      cost: p.cost ?? "",
      description: p.description || "",
      color: p.color || "",
      material: p.material || "",
      stock: p.stock,
      images: p.images || [],
      featured: !!p.featured,
      authenticator: p.authenticator || "",
      certificate_number: p.certificate_number || "",
    });
    setEditing(p);
  };

  const close = () => {
    setEditing(null);
    setForm(empty);
    setImgInput("");
  };

  const addImage = () => {
    const url = imgInput.trim();
    if (!url) return;
    setForm((f) => ({ ...f, images: [...f.images, url] }));
    setImgInput("");
  };

  const removeImage = (i) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        cost: form.cost === "" || form.cost === null ? 0 : Number(form.cost),
        stock: Number(form.stock),
      };
      if (editing === "new") {
        await api.post("/products", payload);
        toast.success("Piece added to the collection.");
      } else {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Piece updated.");
      }
      close();
      load();
    } catch (err) {
      toast.error("Save failed. Verify all required fields.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from the collection?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Removed.");
      load();
    } catch {
      toast.error("Could not remove.");
    }
  };

  const runImport = async () => {
    let parsed;
    try {
      parsed = JSON.parse(importText);
    } catch (err) {
      toast.error("Invalid JSON. Check your brackets and commas.");
      return;
    }
    if (!Array.isArray(parsed) || parsed.length === 0) {
      toast.error("Provide a non-empty JSON array of products.");
      return;
    }
    if (importReplace && !window.confirm(`This will DELETE every existing product and replace with ${parsed.length} new entries. Continue?`)) {
      return;
    }
    setImporting(true);
    try {
      const r = await api.post("/products/import", { products: parsed, replace: importReplace });
      toast.success(`${r.data.inserted} piece${r.data.inserted === 1 ? "" : "s"} imported${r.data.replaced ? ` · ${r.data.deleted} replaced` : ""}.`);
      setShowImport(false);
      setImportText("");
      setImportReplace(false);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Import failed. Check the JSON shape.");
    } finally {
      setImporting(false);
    }
  };

  const clearAll = async () => {
    const phrase = window.prompt('Type DELETE_ALL to permanently remove every product:');
    if (phrase !== "DELETE_ALL") {
      if (phrase !== null) toast.error("Cancelled — phrase mismatch.");
      return;
    }
    try {
      const r = await api.post("/products/clear", { confirm: "DELETE_ALL" });
      toast.success(`${r.data.deleted} piece${r.data.deleted === 1 ? "" : "s"} deleted.`);
      load();
    } catch (err) {
      toast.error("Could not clear collection.");
    }
  };

  const exportShopify = async () => {
    try {
      const r = await api.get("/products/export/csv", { responseType: "blob" });
      // Try to honour the server-suggested filename; fall back to a timestamp.
      let filename = `elegant-affairs-shopify-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`;
      const cd = r.headers?.["content-disposition"];
      const m = cd && /filename="?([^"]+)"?/i.exec(cd);
      if (m && m[1]) filename = m[1];

      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      toast.success("Shopify CSV downloaded.");
    } catch (err) {
      toast.error("Could not export. Try again.");
    }
  };

  // ----- Cloudinary image migration -----
  const fetchMigrateStatus = async () => {
    try {
      const r = await api.get("/products/migrate-images/status");
      setMigrateStatus(r.data);
      return r.data;
    } catch (_) {
      return null;
    }
  };

  // Poll while the modal is open and a migration is running.
  useEffect(() => {
    if (!showMigrate) return undefined;
    let stopped = false;
    const tick = async () => {
      const s = await fetchMigrateStatus();
      if (stopped) return;
      if (s && s.running) {
        setTimeout(tick, 2000);
      } else if (s && s.started_at && s.finished_at) {
        // Reload once a run finishes so new Cloudinary URLs show in the grid.
        load();
      }
    };
    tick();
    return () => { stopped = true; };
  }, [showMigrate]);

  const startMigration = async (dryRun) => {
    setMigrateStarting(true);
    try {
      await api.post("/products/migrate-images", { dry_run: dryRun });
      toast.success(dryRun ? "Dry-run started — no uploads." : "Migration started. This may take ~15–20 min.");
      // Immediately fetch status to flip the UI.
      setTimeout(fetchMigrateStatus, 600);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start migration.");
    } finally {
      setMigrateStarting(false);
    }
  };

  // ----- Auto-classify (Option C) -----
  const previewAutoClassify = async () => {
    setClassifyRunning(true);
    try {
      const r = await api.post("/products/auto-classify", { dry_run: true, only_changed: true });
      setClassifyPreview(r.data);
    } catch (_) {
      toast.error("Could not preview classification.");
    } finally {
      setClassifyRunning(false);
    }
  };

  const applyAutoClassify = async () => {
    if (!classifyPreview || classifyPreview.changes_count === 0) return;
    if (!window.confirm(
      `Apply ${classifyPreview.changes_count} category change${classifyPreview.changes_count === 1 ? "" : "s"}? This rewrites the category on every matched product.`
    )) return;
    setClassifyRunning(true);
    try {
      const r = await api.post("/products/auto-classify", { dry_run: false, only_changed: true });
      toast.success(`${r.data.changes_count} categor${r.data.changes_count === 1 ? "y" : "ies"} updated.`);
      setShowAutoClassify(false);
      setClassifyPreview(null);
      load();
    } catch (_) {
      toast.error("Auto-classify failed.");
    } finally {
      setClassifyRunning(false);
    }
  };

  // ----- Bulk recategorize (Option B) -----
  const toggleSelect = (id) => {
    const s = new Set(selectedIds);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelectedIds(s);
  };

  // ----- Auto-curate Featured -----
  const [curateRunning, setCurateRunning] = useState(false);
  const autoCurateFeatured = async () => {
    if (!window.confirm(
      "Auto-curate the homepage Featured grid?\n\nThis will pick 4 products (entry / mid / premium / hero tiers), prefer top luxury houses, and unfeature everything else."
    )) return;
    setCurateRunning(true);
    try {
      const r = await api.post("/products/auto-curate-featured", {});
      const picks = r.data?.picks || [];
      toast.success(`Featured ${picks.length} pieces · ${picks.map((p) => `${p.brand} $${Math.round(p.price)}`).join(" · ")}`);
      load();
    } catch (_) {
      toast.error("Auto-curate failed.");
    } finally {
      setCurateRunning(false);
    }
  };

  const toggleSelectAll = () => {
    setSelectedIds(selectedIds.size === products.length ? new Set() : new Set(products.map((p) => p.id)));
  };

  const applyBulkCategory = async () => {
    if (selectedIds.size === 0 || !bulkCategory) return;
    if (!window.confirm(`Recategorize ${selectedIds.size} product(s) as "${bulkCategory}"?`)) return;
    try {
      const r = await api.post("/products/bulk-recategorize", {
        product_ids: Array.from(selectedIds),
        category: bulkCategory,
      });
      toast.success(`${r.data.modified} product(s) recategorized.`);
      setSelectedIds(new Set());
      setBulkCategory("");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Bulk update failed.");
    }
  };

  const buildMarkupPayload = () => {
    const pct = Number(markup.percent);
    if (!Number.isFinite(pct)) return null;
    const body = { percent: pct };
    if (markup.scope === "brand" && markup.brand) body.brand = markup.brand;
    if (markup.scope === "category" && markup.category) body.category = markup.category;
    if (markup.scope === "price") {
      if (markup.min_price !== "") body.min_price = Number(markup.min_price);
      if (markup.max_price !== "") body.max_price = Number(markup.max_price);
    }
    return body;
  };

  const previewMarkup = async () => {
    const body = buildMarkupPayload();
    if (!body) {
      toast.error("Enter a valid percent.");
      return;
    }
    setMarkupRunning(true);
    try {
      const r = await api.post("/products/markup", { ...body, dry_run: true });
      setMarkupPreview(r.data);
    } catch {
      toast.error("Preview failed.");
    } finally {
      setMarkupRunning(false);
    }
  };

  const applyMarkup = async () => {
    const body = buildMarkupPayload();
    if (!body) return;
    if (!markupPreview) {
      toast.error("Preview first to confirm scope.");
      return;
    }
    if (!window.confirm(
      `Apply ${body.percent >= 0 ? "+" : ""}${body.percent}% to ${markupPreview.affected} product${markupPreview.affected === 1 ? "" : "s"}? This rewrites every matching price.`
    )) return;
    setMarkupRunning(true);
    try {
      const r = await api.post("/products/markup", body);
      toast.success(`${r.data.affected} price${r.data.affected === 1 ? "" : "s"} updated.`);
      setShowMarkup(false);
      setMarkupPreview(null);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Markup failed.");
    } finally {
      setMarkupRunning(false);
    }
  };

  // Distinct brand/category lists from the loaded products — used in the modal.
  const brandOptions = Array.from(new Set(products.map((p) => p.brand).filter(Boolean))).sort();
  const categoryOptions = Array.from(new Set(products.map((p) => p.category).filter(Boolean))).sort();

  return (
    <AdminLayout
      title="Inventory."
      action={
        <div className="flex flex-wrap items-center gap-2">
          {products.length > 0 && (
            <button
              onClick={() => { setShowMarkup(true); setMarkupPreview(null); }}
              className="btn-ghost"
              data-testid="admin-open-markup"
              title="Apply a percentage markup across multiple products at once"
            >
              <Percent size={14} strokeWidth={1.5} /> Markup
            </button>
          )}
          {products.length > 0 && (
            <button
              onClick={exportShopify}
              className="btn-ghost"
              data-testid="admin-export-shopify"
              title="Download a Shopify-importable CSV of every product"
            >
              <Download size={14} strokeWidth={1.5} /> Export CSV
            </button>
          )}
          {products.length > 0 && (
            <button
              onClick={() => { setShowAutoClassify(true); previewAutoClassify(); }}
              className="btn-ghost"
              data-testid="admin-open-auto-classify"
              title="Suggest category changes based on product names"
            >
              <Wand2 size={14} strokeWidth={1.5} /> Auto-Classify
            </button>
          )}
          {products.length > 0 && (
            <button
              onClick={autoCurateFeatured}
              disabled={curateRunning}
              className="btn-ghost"
              data-testid="admin-auto-curate-featured"
              title="Re-balance the homepage Featured grid across entry / mid / premium / hero price tiers"
            >
              {curateRunning ? <Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> : <Sparkles size={14} strokeWidth={1.5} />}
              {curateRunning ? "Curating…" : "Auto-curate Featured"}
            </button>
          )}
          {products.length > 0 && (
            <button
              onClick={() => { setShowMigrate(true); fetchMigrateStatus(); }}
              className="btn-ghost"
              data-testid="admin-open-migrate"
              title="Migrate all product images from Shopify CDN to Cloudinary"
            >
              <Cloud size={14} strokeWidth={1.5} /> Migrate Images
            </button>
          )}
          <button
            onClick={() => setShowImport(true)}
            className="btn-ghost"
            data-testid="admin-open-import"
          >
            <Upload size={14} strokeWidth={1.5} /> Import
          </button>
          {products.length > 0 && (
            <button
              onClick={clearAll}
              className="btn-ghost hover:!bg-[var(--mn-accent)] hover:!border-[var(--mn-accent)] hover:!text-white"
              data-testid="admin-clear-all"
              title="Delete every product"
            >
              <Trash size={14} strokeWidth={1.5} /> Clear all
            </button>
          )}
          <button onClick={startNew} className="btn-primary" data-testid="admin-new-product">
            <Plus size={16} strokeWidth={1.5} /> New Piece
          </button>
        </div>
      }
    >
      <div data-testid="admin-page">

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="aspect-[4/5] bg-neutral-200/60 animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="border border-dashed border-[var(--mn-border-strong)] py-32 text-center" data-testid="admin-empty">
            <p className="font-display text-3xl">Your collection is empty.</p>
            <p className="text-[var(--mn-ink-2)] mt-3">Add the first bag to your storefront.</p>
            <button onClick={startNew} className="btn-primary mt-8" data-testid="admin-empty-new">
              <Plus size={16} strokeWidth={1.5} /> Add first piece
            </button>
          </div>
        ) : (
          <div className="border-y border-[var(--mn-border)]">
            <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-4 eyebrow text-[var(--mn-muted)]">
              <div className="col-span-1 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedIds.size > 0 && selectedIds.size === products.length}
                  ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < products.length; }}
                  onChange={toggleSelectAll}
                  className="cursor-pointer"
                  aria-label="Select all"
                  data-testid="admin-select-all"
                />
              </div>
              <div className="col-span-3">Name</div>
              <div className="col-span-2">Category</div>
              <div className="col-span-1 text-right">Price</div>
              <div className="col-span-1 text-right">Cost</div>
              <div className="col-span-1 text-right">Margin</div>
              <div className="col-span-1 text-right">Stock</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            <ul className="divide-y divide-[var(--mn-border)]">
              {products.map((p) => {
                const cost = Number(p.cost) || 0;
                const price = Number(p.price) || 0;
                const marginPct = price > 0 ? ((price - cost) / price) * 100 : 0;
                const marginClass =
                  cost <= 0
                    ? "text-[var(--mn-muted)]"
                    : marginPct >= 35
                    ? "text-emerald-700"
                    : marginPct >= 20
                    ? "text-amber-700"
                    : "text-[var(--mn-accent)]";
                return (
                <li key={p.id} className={`grid grid-cols-12 gap-4 px-4 py-4 items-center ${selectedIds.has(p.id) ? "bg-neutral-50" : ""}`} data-testid={`admin-row-${p.id}`}>
                  <div className="col-span-2 md:col-span-1 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      className="cursor-pointer shrink-0"
                      aria-label="Select"
                      data-testid={`admin-select-${p.id}`}
                    />
                    <div className="aspect-square bg-neutral-100 overflow-hidden flex-1 min-w-0">
                      {p.images && p.images[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
                    </div>
                  </div>
                  <div className="col-span-10 md:col-span-3 min-w-0">
                    <p className="eyebrow truncate">{p.brand}</p>
                    <p className="font-display text-lg mt-1 leading-tight line-clamp-2">{p.name}</p>
                  </div>
                  <div className="col-span-4 md:col-span-2 text-sm">{p.category}</div>
                  <div className="col-span-3 md:col-span-1 font-mono-luxe text-sm md:text-right">{formatPrice(p.price)}</div>
                  <div className="col-span-3 md:col-span-1 font-mono-luxe text-xs md:text-right text-[var(--mn-ink-2)]">
                    {cost > 0 ? formatPrice(cost) : "—"}
                  </div>
                  <div className={`col-span-3 md:col-span-1 font-mono-luxe text-xs md:text-right font-semibold ${marginClass}`} data-testid={`admin-margin-${p.id}`}>
                    {cost > 0 ? `${marginPct.toFixed(0)}%` : "—"}
                  </div>
                  <div className="col-span-3 md:col-span-1 font-mono-luxe text-sm md:text-right">{p.stock}</div>
                  <div className="col-span-12 md:col-span-2 flex md:justify-end gap-2">
                    <button onClick={() => startEdit(p)} className="btn-ghost !px-4 !py-2" data-testid={`admin-edit-${p.id}`}>
                      <Edit3 size={14} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => remove(p)} className="btn-ghost !px-4 !py-2 hover:!bg-[var(--mn-accent)] hover:!border-[var(--mn-accent)]" data-testid={`admin-delete-${p.id}`}>
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* Sticky bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[var(--mn-ink)] text-[var(--mn-bg)] px-6 py-3 shadow-2xl flex items-center gap-4 flex-wrap" data-testid="bulk-bar">
          <span className="text-sm font-mono-luxe">
            {selectedIds.size} selected
          </span>
          <div className="h-4 w-px bg-white/20" />
          <Tags size={14} strokeWidth={1.5} />
          <select
            value={bulkCategory}
            onChange={(e) => setBulkCategory(e.target.value)}
            className="bg-transparent border border-white/30 text-white text-xs px-2 py-1 focus:border-white focus:outline-none"
            data-testid="bulk-category-select"
          >
            <option value="" className="text-black">Recategorize to…</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c} className="text-black">{c}</option>
            ))}
          </select>
          <button
            onClick={applyBulkCategory}
            disabled={!bulkCategory}
            className="text-[10px] uppercase tracking-[0.22em] bg-white text-[var(--mn-ink)] px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="bulk-apply"
          >
            Apply
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-[10px] uppercase tracking-[0.22em] text-white/60 hover:text-white"
            data-testid="bulk-clear"
          >
            Clear
          </button>
        </div>
      )}

      {/* Auto-classify modal */}
      {showAutoClassify && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[60]" onClick={() => setShowAutoClassify(false)} data-testid="classify-backdrop" />
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-6 pointer-events-none">
            <div className="bg-[var(--mn-bg)] w-full max-w-2xl pointer-events-auto border border-[var(--mn-border)] max-h-[90vh] flex flex-col" data-testid="classify-modal">
              <div className="flex items-center justify-between px-8 py-6 border-b border-[var(--mn-border)] shrink-0">
                <div>
                  <p className="eyebrow">Smart classify</p>
                  <h2 className="font-display text-3xl mt-1 tracking-tight">Auto-Classify Categories</h2>
                </div>
                <button onClick={() => setShowAutoClassify(false)} className="p-2" data-testid="classify-close" aria-label="Close">
                  <X size={20} strokeWidth={1.5} />
                </button>
              </div>
              <div className="px-8 py-6 overflow-y-auto flex-1">
                <p className="text-sm text-[var(--mn-ink-2)] leading-relaxed mb-6">
                  Suggests category changes by scanning product names for specific keywords (Tote, Clutch, Crossbody,
                  Bucket, Backpack, Mini, Top Handle, Pochette, Hobo). Generic "Handbag" is never auto-assigned —
                  products without a specific match keep their current category.
                </p>
                {classifyRunning && !classifyPreview && (
                  <div className="text-center py-12 text-[var(--mn-ink-2)]">
                    <Loader2 size={20} className="animate-spin mx-auto mb-3" />
                    Scanning catalog…
                  </div>
                )}
                {classifyPreview && (
                  <>
                    <div className="grid grid-cols-3 gap-4 mb-6 pb-6 border-b border-[var(--mn-border)]">
                      <div>
                        <p className="eyebrow">Scanned</p>
                        <p className="font-mono-luxe text-2xl mt-1">{classifyPreview.total_scanned}</p>
                      </div>
                      <div>
                        <p className="eyebrow">Changes</p>
                        <p className="font-mono-luxe text-2xl mt-1">{classifyPreview.changes_count}</p>
                      </div>
                      <div>
                        <p className="eyebrow">Unchanged</p>
                        <p className="font-mono-luxe text-2xl mt-1 text-[var(--mn-ink-2)]">{classifyPreview.total_scanned - classifyPreview.changes_count}</p>
                      </div>
                    </div>
                    {classifyPreview.changes_count > 0 ? (
                      <>
                        <p className="eyebrow mb-3">By transition</p>
                        <ul className="space-y-1.5 mb-6">
                          {Object.entries(classifyPreview.by_transition).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                            <li key={k} className="flex justify-between text-sm">
                              <span className="font-mono-luxe text-xs">{k}</span>
                              <span className="font-mono-luxe">{v}</span>
                            </li>
                          ))}
                        </ul>
                        <p className="eyebrow mb-3">Preview (first 50)</p>
                        <ul className="space-y-1.5 text-xs max-h-72 overflow-y-auto border border-[var(--mn-border)] p-3">
                          {classifyPreview.preview.map((c) => (
                            <li key={c.id} className="flex justify-between gap-3">
                              <span className="truncate"><span className="text-[var(--mn-ink-2)]">{c.brand}</span> — {c.name}</span>
                              <span className="font-mono-luxe shrink-0">
                                <span className="text-[var(--mn-ink-2)]">{c.current}</span>
                                <span className="mx-1">→</span>
                                <span>{c.suggested}</span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <p className="text-center py-8 text-[var(--mn-ink-2)]">No changes needed. Catalog is well-categorized.</p>
                    )}
                  </>
                )}
              </div>
              <div className="px-8 py-6 border-t border-[var(--mn-border)] flex justify-end gap-3 shrink-0">
                <button onClick={() => setShowAutoClassify(false)} className="btn-ghost" data-testid="classify-cancel">
                  Close
                </button>
                {classifyPreview && classifyPreview.changes_count > 0 && (
                  <button
                    onClick={applyAutoClassify}
                    disabled={classifyRunning}
                    className="btn-primary"
                    data-testid="classify-apply"
                  >
                    {classifyRunning ? <><Loader2 size={14} className="animate-spin" /> Applying…</> : <><Wand2 size={14} strokeWidth={1.5} /> Apply {classifyPreview.changes_count} change{classifyPreview.changes_count === 1 ? "" : "s"}</>}
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Image Migration Modal */}
      {showMigrate && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[60]" onClick={() => setShowMigrate(false)} data-testid="migrate-backdrop" />
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-6 pointer-events-none">
            <div className="bg-[var(--mn-bg)] w-full max-w-2xl pointer-events-auto border border-[var(--mn-border)]" data-testid="migrate-modal">
              <div className="flex items-center justify-between px-8 py-6 border-b border-[var(--mn-border)]">
                <div>
                  <p className="eyebrow">Cloudinary</p>
                  <h2 className="font-display text-3xl mt-1 tracking-tight">Migrate Images</h2>
                </div>
                <button onClick={() => setShowMigrate(false)} className="p-2" data-testid="migrate-close" aria-label="Close">
                  <X size={20} strokeWidth={1.5} />
                </button>
              </div>
              <div className="px-8 py-8 space-y-6">
                <p className="text-sm text-[var(--mn-ink-2)] leading-relaxed">
                  This downloads every product image currently hotlinked from external sources
                  (Shopify CDN) and uploads them to your own Cloudinary account under{" "}
                  <code className="font-mono-luxe text-xs bg-neutral-100 px-1.5 py-0.5">elegant-affairs/products/</code>.
                  The database is then updated with the new Cloudinary URLs. Images already on
                  Cloudinary are skipped automatically — safe to re-run.
                </p>

                <MigrateStatusPanel status={migrateStatus} />

                <div className="flex flex-wrap gap-3 pt-2">
                  <button
                    onClick={() => startMigration(true)}
                    disabled={migrateStarting || (migrateStatus && migrateStatus.running)}
                    className="btn-ghost"
                    data-testid="migrate-dry-run"
                  >
                    Dry-run preview
                  </button>
                  <button
                    onClick={() => startMigration(false)}
                    disabled={migrateStarting || (migrateStatus && migrateStatus.running)}
                    className="btn-primary"
                    data-testid="migrate-run"
                  >
                    {migrateStatus && migrateStatus.running ? (
                      <><Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> Running…</>
                    ) : (
                      <><Cloud size={14} strokeWidth={1.5} /> Run migration</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Editor Drawer */}
      {editing && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[60]" onClick={close} data-testid="admin-form-backdrop" />
          <aside className="fixed top-0 right-0 h-full w-full sm:max-w-2xl bg-[var(--mn-bg)] z-[70] overflow-y-auto" data-testid="admin-form">
            <div className="flex items-center justify-between px-8 py-6 border-b border-[var(--mn-border)] sticky top-0 bg-[var(--mn-bg)] z-10">
              <div>
                <p className="eyebrow">{editing === "new" ? "New" : "Edit"}</p>
                <h2 className="font-display text-3xl mt-1">
                  {editing === "new" ? "Add a piece" : editing.name}
                </h2>
              </div>
              <button onClick={close} className="p-2" data-testid="admin-form-close" aria-label="Close">
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>
            <form onSubmit={save} className="px-8 py-8 space-y-5">
              <Field label="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} testid="admin-field-name" />
              <Field label="Brand" required value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} testid="admin-field-brand" />
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="eyebrow block mb-2">Category</span>
                  <select
                    className="field-input"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    data-testid="admin-field-category"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </label>
                <Field label="Price (USD)" type="number" min="0" step="0.01" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} testid="admin-field-price" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Wholesale cost (USD)" type="number" min="0" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} testid="admin-field-cost" />
                <Field label="Stock" type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} testid="admin-field-stock" />
              </div>
              {form.price && form.cost && Number(form.cost) > 0 && (
                <div className="px-4 py-3 border border-[var(--mn-border)] bg-neutral-50/40 text-sm" data-testid="admin-field-margin-display">
                  <span className="eyebrow">Margin preview</span>
                  <p className="font-mono-luxe text-base mt-1">
                    ${Number(form.price).toFixed(2)} − ${Number(form.cost).toFixed(2)} = <strong>${(Number(form.price) - Number(form.cost)).toFixed(2)}</strong>
                    <span className="text-[var(--mn-ink-2)] ml-2">
                      ({Number(form.price) > 0 ? (((Number(form.price) - Number(form.cost)) / Number(form.price)) * 100).toFixed(1) : 0}%)
                    </span>
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <Field label="Colour" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} testid="admin-field-color" />
                <Field label="Material" value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} testid="admin-field-material" />
              </div>
              <label className="block">
                <span className="eyebrow block mb-2">Description</span>
                <textarea
                  rows="4"
                  className="field-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="admin-field-description"
                />
              </label>

              {/* Authentication */}
              <div className="border-t border-[var(--mn-border)] pt-5">
                <p className="eyebrow mb-1">Authentication</p>
                <p className="text-xs text-[var(--mn-ink-2)] mb-4 leading-relaxed">
                  Optional. Fill these in once a third-party authenticator has verified this bag — they appear as a badge on the product page.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <Field
                    label="Authenticated by"
                    placeholder="e.g. Real Authentication"
                    value={form.authenticator}
                    onChange={(e) => setForm({ ...form, authenticator: e.target.value })}
                    testid="admin-field-authenticator"
                  />
                  <Field
                    label="Certificate #"
                    placeholder="e.g. RA-12345"
                    value={form.certificate_number}
                    onChange={(e) => setForm({ ...form, certificate_number: e.target.value })}
                    testid="admin-field-certificate"
                  />
                </div>
              </div>

              {/* Images */}
              <div>
                <span className="eyebrow block mb-2">Images (URLs)</span>
                <div className="flex gap-2">
                  <input
                    placeholder="https://…"
                    value={imgInput}
                    onChange={(e) => setImgInput(e.target.value)}
                    className="field-input flex-1"
                    data-testid="admin-image-input"
                  />
                  <button type="button" onClick={addImage} className="btn-ghost !px-4" data-testid="admin-image-add">
                    <ImagePlus size={16} strokeWidth={1.5} /> Add
                  </button>
                </div>
                {form.images.length > 0 && (
                  <div className="grid grid-cols-4 gap-3 mt-4" data-testid="admin-images-preview">
                    {form.images.map((u, i) => (
                      <div key={i} className="relative aspect-square bg-neutral-100 overflow-hidden">
                        <img src={u} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImage(i)}
                          className="absolute top-1 right-1 bg-[var(--mn-ink)] text-white p-1"
                          aria-label="Remove image"
                          data-testid={`admin-image-remove-${i}`}
                        >
                          <X size={12} strokeWidth={1.5} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  className="w-4 h-4 accent-[var(--mn-ink)]"
                  data-testid="admin-field-featured"
                />
                <span className="text-sm uppercase tracking-[0.18em]">Mark as featured</span>
              </label>

              <div className="flex gap-3 pt-4 border-t border-[var(--mn-border)]">
                <button type="button" onClick={close} className="btn-ghost flex-1 justify-center" data-testid="admin-cancel">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center" data-testid="admin-save">
                  {saving ? "Saving…" : editing === "new" ? "Create Piece" : "Save Changes"}
                </button>
              </div>
            </form>
          </aside>
        </>
      )}
      {/* Bulk Markup modal */}
      {showMarkup && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-[60]"
            onClick={() => !markupRunning && setShowMarkup(false)}
            data-testid="admin-markup-backdrop"
          />
          <div
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(620px,calc(100vw-32px))] max-h-[90vh] overflow-y-auto bg-[var(--mn-bg)] z-[70] border border-[var(--mn-border-strong)] shadow-2xl"
            data-testid="admin-markup-modal"
          >
            <div className="flex items-center justify-between px-8 py-6 border-b border-[var(--mn-border)]">
              <div>
                <p className="eyebrow">Bulk pricing</p>
                <h2 className="font-display text-3xl mt-1">Apply markup</h2>
              </div>
              <button
                onClick={() => !markupRunning && setShowMarkup(false)}
                className="p-2"
                aria-label="Close"
                data-testid="admin-markup-close"
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            <div className="px-8 py-7 space-y-6">
              <p className="text-sm text-[var(--mn-ink-2)] leading-relaxed">
                Apply a percentage adjustment (positive or negative) across products that match the
                scope below. Use this for tariffs, currency adjustments, or season-end markdowns.
              </p>

              {/* Percent + Scope */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="eyebrow">Percent</span>
                  <div className="mt-2 relative">
                    <input
                      type="number"
                      step="0.5"
                      value={markup.percent}
                      onChange={(e) => { setMarkup({ ...markup, percent: e.target.value }); setMarkupPreview(null); }}
                      className="field-input pr-8"
                      data-testid="admin-markup-percent"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--mn-ink-2)] text-sm">%</span>
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-muted)] mt-2">
                    Use negative for markdowns. Tariff in this catalog is typically 15%.
                  </p>
                </label>

                <label className="block">
                  <span className="eyebrow">Scope</span>
                  <select
                    value={markup.scope}
                    onChange={(e) => { setMarkup({ ...markup, scope: e.target.value }); setMarkupPreview(null); }}
                    className="field-input mt-2"
                    data-testid="admin-markup-scope"
                  >
                    <option value="all">All products</option>
                    <option value="brand">By brand</option>
                    <option value="category">By category</option>
                    <option value="price">By price range</option>
                  </select>
                </label>
              </div>

              {markup.scope === "brand" && (
                <label className="block">
                  <span className="eyebrow">Brand</span>
                  <select
                    value={markup.brand}
                    onChange={(e) => { setMarkup({ ...markup, brand: e.target.value }); setMarkupPreview(null); }}
                    className="field-input mt-2"
                    data-testid="admin-markup-brand"
                  >
                    <option value="">— Select a brand —</option>
                    {brandOptions.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </label>
              )}

              {markup.scope === "category" && (
                <label className="block">
                  <span className="eyebrow">Category</span>
                  <select
                    value={markup.category}
                    onChange={(e) => { setMarkup({ ...markup, category: e.target.value }); setMarkupPreview(null); }}
                    className="field-input mt-2"
                    data-testid="admin-markup-category"
                  >
                    <option value="">— Select a category —</option>
                    {categoryOptions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </label>
              )}

              {markup.scope === "price" && (
                <div className="grid grid-cols-2 gap-4">
                  <label className="block">
                    <span className="eyebrow">Min price ($)</span>
                    <input
                      type="number"
                      value={markup.min_price}
                      onChange={(e) => { setMarkup({ ...markup, min_price: e.target.value }); setMarkupPreview(null); }}
                      className="field-input mt-2"
                      placeholder="e.g. 500"
                      data-testid="admin-markup-min"
                    />
                  </label>
                  <label className="block">
                    <span className="eyebrow">Max price ($)</span>
                    <input
                      type="number"
                      value={markup.max_price}
                      onChange={(e) => { setMarkup({ ...markup, max_price: e.target.value }); setMarkupPreview(null); }}
                      className="field-input mt-2"
                      placeholder="(none)"
                      data-testid="admin-markup-max"
                    />
                  </label>
                </div>
              )}

              {/* Preview block */}
              {markupPreview && (
                <div className="border border-[var(--mn-border-strong)] p-5" data-testid="admin-markup-preview">
                  <p className="eyebrow">Preview</p>
                  <p className="font-display text-2xl mt-1 leading-tight">
                    {markupPreview.affected} product{markupPreview.affected === 1 ? "" : "s"} match
                  </p>
                  {markupPreview.sample && markupPreview.sample.length > 0 ? (
                    <ul className="mt-4 space-y-2 text-sm font-mono-luxe">
                      {markupPreview.sample.map((s, i) => (
                        <li key={i} className="flex items-center gap-3 text-[var(--mn-ink-2)]">
                          <span className="line-through opacity-50">${Number(s.old).toLocaleString("en-US")}</span>
                          <span className="text-[var(--mn-muted)]">→</span>
                          <span className="text-[var(--mn-ink)] font-semibold">${Number(s.new).toLocaleString("en-US")}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-[var(--mn-muted)] italic mt-3">No products match this scope.</p>
                  )}
                </div>
              )}

              {/* Action row */}
              <div className="flex gap-3 pt-4 border-t border-[var(--mn-border)]">
                <button
                  type="button"
                  onClick={() => setShowMarkup(false)}
                  disabled={markupRunning}
                  className="btn-ghost flex-1 justify-center"
                  data-testid="admin-markup-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={previewMarkup}
                  disabled={markupRunning}
                  className="btn-ghost flex-1 justify-center"
                  data-testid="admin-markup-preview-btn"
                >
                  {markupRunning ? "…" : "Preview"}
                </button>
                <button
                  type="button"
                  onClick={applyMarkup}
                  disabled={markupRunning || !markupPreview || markupPreview.affected === 0}
                  className="btn-primary flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="admin-markup-apply"
                >
                  {markupRunning ? "Applying…" : `Apply${markupPreview ? ` to ${markupPreview.affected}` : ""}`}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Bulk Import Drawer */}
      {showImport && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-[60]"
            onClick={() => !importing && setShowImport(false)}
            data-testid="admin-import-backdrop"
          />
          <aside
            className="fixed top-0 right-0 h-full w-full sm:max-w-2xl bg-[var(--mn-bg)] z-[70] overflow-y-auto"
            data-testid="admin-import-drawer"
          >
            <div className="flex items-center justify-between px-8 py-6 border-b border-[var(--mn-border)] sticky top-0 bg-[var(--mn-bg)] z-10">
              <div>
                <p className="eyebrow">Bulk</p>
                <h2 className="font-display text-3xl mt-1">Import collection</h2>
              </div>
              <button
                onClick={() => !importing && setShowImport(false)}
                className="p-2"
                data-testid="admin-import-close"
                aria-label="Close"
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            <div className="px-8 py-8 space-y-6">
              <p className="text-sm text-[var(--mn-ink-2)] leading-relaxed">
                Paste a JSON array of pieces below. Each entry is validated against the same
                schema as the &ldquo;New piece&rdquo; form. Required fields:{" "}
                <code className="font-mono-luxe text-xs">name</code>,{" "}
                <code className="font-mono-luxe text-xs">brand</code>,{" "}
                <code className="font-mono-luxe text-xs">category</code>,{" "}
                <code className="font-mono-luxe text-xs">price</code>.
              </p>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="eyebrow">Products JSON</span>
                  <button
                    type="button"
                    onClick={() => setImportText(SAMPLE_IMPORT_JSON)}
                    className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-ink-2)] hover:text-[var(--mn-accent)]"
                    data-testid="admin-import-sample"
                  >
                    Insert sample
                  </button>
                </div>
                <textarea
                  rows="16"
                  className="field-input font-mono-luxe text-xs leading-relaxed"
                  placeholder={SAMPLE_IMPORT_JSON}
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  data-testid="admin-import-textarea"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer p-4 border border-[var(--mn-border-strong)]">
                <input
                  type="checkbox"
                  checked={importReplace}
                  onChange={(e) => setImportReplace(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[var(--mn-accent)]"
                  data-testid="admin-import-replace"
                />
                <div>
                  <p className="text-sm uppercase tracking-[0.18em] text-[var(--mn-ink)]">
                    Replace existing collection
                  </p>
                  <p className="text-xs text-[var(--mn-ink-2)] mt-1 leading-relaxed">
                    When checked, every existing product will be permanently deleted before the new
                    entries are inserted. Leave unchecked to add to the current catalog.
                  </p>
                </div>
              </label>

              <div className="flex gap-3 pt-4 border-t border-[var(--mn-border)]">
                <button
                  type="button"
                  onClick={() => setShowImport(false)}
                  disabled={importing}
                  className="btn-ghost flex-1 justify-center"
                  data-testid="admin-import-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={runImport}
                  disabled={importing || !importText.trim()}
                  className="btn-primary flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="admin-import-run"
                >
                  {importing ? "Importing…" : importReplace ? "Replace & Import" : "Import"}
                </button>
              </div>
            </div>
          </aside>
        </>
      )}
    </AdminLayout>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      <input className="field-input" data-testid={testid} {...props} />
    </label>
  );
}

function MigrateStatusPanel({ status }) {
  if (!status || (!status.started_at && !status.running)) {
    return (
      <div className="border border-dashed border-[var(--mn-border-strong)] p-6 text-sm text-[var(--mn-ink-2)]" data-testid="migrate-idle">
        No migration has run yet. Start with a <em>Dry-run preview</em> to see how many images will move.
      </div>
    );
  }
  const pct = status.total_products
    ? Math.round((status.processed_products / status.total_products) * 100)
    : 0;
  const done = !status.running && status.finished_at;
  return (
    <div className="border border-[var(--mn-border)] p-6 space-y-4" data-testid="migrate-status">
      <div className="flex items-center justify-between">
        <p className="eyebrow">
          {status.running
            ? (status.dry_run ? "Dry-run in progress" : "Migration in progress")
            : status.dry_run ? "Dry-run complete" : "Migration complete"}
        </p>
        <p className="text-xs font-mono-luxe text-[var(--mn-ink-2)]" data-testid="migrate-progress">
          {status.processed_products}/{status.total_products} products · {pct}%
        </p>
      </div>
      <div className="h-1.5 bg-neutral-200 overflow-hidden">
        <div
          className="h-full bg-[var(--mn-ink)] transition-all duration-500"
          style={{ width: `${pct}%` }}
          data-testid="migrate-progress-bar"
        />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <Stat label="Images seen" value={status.total_images} testid="migrate-stat-total" />
        <Stat label="Migrated" value={status.migrated_images} testid="migrate-stat-migrated" />
        <Stat label="Already on Cloudinary" value={status.already_migrated} testid="migrate-stat-skipped" />
        <Stat label="Failed" value={status.failed_images} testid="migrate-stat-failed" warn={status.failed_images > 0} />
      </div>
      {done && status.dry_run && (
        <p className="text-xs text-[var(--mn-muted)]">
          Dry-run only — no uploads were performed. Click <em>Run migration</em> to do it for real.
        </p>
      )}
      {status.failed_samples && status.failed_samples.length > 0 && (
        <details className="text-xs text-[var(--mn-ink-2)]">
          <summary className="cursor-pointer">Failed URL samples</summary>
          <ul className="mt-2 space-y-1 font-mono-luxe text-[10px] break-all">
            {status.failed_samples.map((u) => (<li key={u}>{u}</li>))}
          </ul>
        </details>
      )}
      {status.last_error && (
        <p className="text-xs text-[var(--mn-accent)]">Error: {status.last_error}</p>
      )}
    </div>
  );
}

function Stat({ label, value, testid, warn = false }) {
  return (
    <div data-testid={testid}>
      <p className="eyebrow">{label}</p>
      <p className={`font-mono-luxe text-xl mt-1 ${warn ? "text-[var(--mn-accent)]" : ""}`}>{value}</p>
    </div>
  );
}
